import React, { useState } from "react";
import { FOCUS_THEMES, useFocusTheme } from "../theme/FocusThemeContext";

// Two wisps, each wrapping a tinted ::before (accent)/::after (ink) layer
// that pulse opacity out of phase (see `.smoke-*` rules in global.css) and
// drift via a transform-only keyframe. Plain soft radial-gradient blobs —
// no SVG filter — so the whole thing is compositor-cheap.
const SMOKE_WISPS = [
  { name: "a", drift: 52, pulseIn: 23, pulseOut: 29 },
  { name: "b", drift: 61, pulseIn: 31, pulseOut: 19 },
];

// Fixed full-viewport backdrop shared by every section on the home page.
// All themes stay mounted and crossfade via the .active opacity transition.
export default function FocusBackground() {
  const { focus } = useFocusTheme();

  // Negative delays start each wisp — and each wisp's two color-pulse layers —
  // mid-cycle at a random point instead of everything beginning in lockstep
  // at t=0. Computed once per mount (lazy init) so the "random" start is
  // stable for the life of the page.
  const [randomized] = useState(() => ({
    delays: Object.fromEntries(
      SMOKE_WISPS.map(({ name, drift, pulseIn, pulseOut }) => [
        name,
        {
          "--drift-delay": `${-Math.random() * drift}s`,
          "--pulse-delay-1": `${-Math.random() * pulseIn}s`,
          "--pulse-delay-2": `${-Math.random() * pulseOut}s`,
        },
      ])
    ),
  }));

  return (
    <div className="focus-backdrop" aria-hidden="true">
      {Object.entries(FOCUS_THEMES).map(([state, t]) => (
        <div
          key={state}
          className={[
            "focus-layer",
            state === "Sleep" ? "focus-layer--sleep" : "",
            state === focus ? "active" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          // backgroundImage (not the background shorthand) so the CSS
          // background-size set in global.css isn't reset
          style={{ backgroundImage: t.gradient }}
        />
      ))}

      {SMOKE_WISPS.map(({ name }) => (
        <div
          key={name}
          className={`smoke smoke-${name}`}
          style={randomized.delays[name]}
        />
      ))}
    </div>
  );
}
